<script setup lang="ts">
import type { PropType } from "vue";
import { capitalize } from "vue";

const props = defineProps({
	title: {
		type: String,
		default: "Title"
	},
	placeholder: {
		type: String,
		default: "Placeholder"
	},
	whiteList: {
		type: Array as PropType<string[]>,
		default () {
			return [];
		}
	},
	error: {
		type: String,
		default: ""
	},
	required: {
		type: Boolean,
		default: false
	},
	modelValue: {
		type: Array as PropType<string[]>,
		default () {
			return [];
		}
	}
});
const emit = defineEmits(["update:modelValue"]);

const id = useId();
const isFocused = ref(false);
const tagsSet = ref<Set<string>>(new Set(props.modelValue));

const modelValueAccessor = computed({
	get () {
		return props.modelValue;
	},
	set (value) {
		emit("update:modelValue", value);
	}
});
const tagsArr = computed(() => [...tagsSet.value]);
const focusedClasses = computed(() => isFocused.value && "elevation-cta-shadow-border");
const errorClasses = computed(() => !isFocused.value && !!props.error && "border-rose-500");

const isWhitelisted = (tag: string): boolean => {
	const { whiteList } = props;
	if (!whiteList.length) {
		return true;
	}

	return whiteList.includes(tag);
};
const handleInput = (event: Event, ignoreComma = false) => {
	const value: string = (event.target as any).value;
	if (!ignoreComma && !value.includes(",")) {
		return;
	}

	(event.target as any).value = "";
	value.split(",").map(tag => tag.trim().toLowerCase()).forEach((tag) => {
		if (!tag || !isWhitelisted(tag)) {
			return;
		}
		tagsSet.value.add(tag);
	});
	modelValueAccessor.value = tagsArr.value;
};
const removeTag = (tag: string) => {
	tagsSet.value.delete(tag);
	modelValueAccessor.value = tagsArr.value;
};
const clearTags = () => {
	tagsSet.value.clear();
	modelValueAccessor.value = tagsArr.value;
};
</script>

<template>
	<div>
		<div
			class="flex flex-col p-3 bg-gray-50 border border-gray-200 rounded-sm duration-75"
			:class="[focusedClasses, errorClasses]"
		>
			<div class="flex justify-between text-gray-500">
				<label
					:for="id"
					class="font-semibold text-sm"
				>
					<span>{{ title }}</span>
					<span
						v-if="required"
						class="text-rose-500"
					>*</span>
				</label>
				<NuxtIcon
					name="x-16"
					class="hover:cursor-pointer"
					@click="clearTags"
				/>
			</div>
			<div class="mt-1 flex flex-wrap gap-1 truncate">
				<div
					v-for="(tag, i) in tagsSet"
					:key="i"
					class="flex items-center bg-gray-900 rounded-xs px-2 py-1 w-max truncate"
				>
					<span class="font-semibold text-sm text-gray-0 truncate">
						{{ tag }}
					</span>
					<NuxtIcon
						name="x-16"
						class="ml-1 hover:cursor-pointer text-gray-400"
						@click="removeTag(tag)"
					/>
				</div>
				<input
					:id="id"
					type="text"
					class="text-base text-gray-900 focus-visible:outline-none bg-transparent"
					:placeholder="placeholder"
					@keydown.enter.prevent="handleInput($event, true)"
					@input="handleInput"
					@focusin="(isFocused = true)"
					@focusout="handleInput($event, true), (isFocused = false)"
				/>
			</div>
		</div>
		<div
			v-if="error"
			class="font-semibold text-sm text-rose-500 mt-1"
		>
			{{ capitalize(error) }}
		</div>
	</div>
</template>
